import _objectWithoutProperties from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useMemo } from 'react';
import styled, { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { isPlainObject } from 'lodash';
import { spacingLabels, typographyVariantLabels } from '../../themes';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { cssPostfix } from '../../utils/cssPostfix';

var postfix = function postfix(propertyName) {
  return "--".concat(propertyName, "-").concat(cssPostfix);
};

var withUnit = function withUnit(value, unit) {
  return value !== undefined ? value + unit : value;
};

function generateCustomPropertyNamesFromTheme(obj, parentKey) {
  var newObj = Object.create(null);

  for (var _i = 0, _Object$keys = Object.keys(obj); _i < _Object$keys.length; _i++) {
    var key = _Object$keys[_i];
    var newKey = parentKey ? [parentKey, key].join('-') : key;

    if (isPlainObject(obj[key])) {
      newObj[key] = generateCustomPropertyNamesFromTheme(obj[key], newKey);
    } else {
      newObj[key] = postfix(newKey);
    }
  }

  return newObj;
}

function assignDeepFlat(keys, values) {
  return Object.keys(keys).reduce(function (acc, key) {
    if (isPlainObject(keys[key])) {
      return _objectSpread({}, acc, {}, assignDeepFlat(keys[key], values[key]));
    } else {
      return _objectSpread({}, acc, _defineProperty({}, keys[key], values[key]));
    }
  }, {});
}

export var CustomPropertyProvider = styled.div.withConfig({
  // stylelint already intents to parse the config as CSS
  // stylelint-disable-next-line property-no-unknown
  shouldForwardProp: function shouldForwardProp(prop) {
    return ['children', 'className'].includes(prop);
  }
})(function (_ref) {
  var typography = _ref.typography,
      spacing = _ref.spacing,
      breakpoints = _ref.breakpoints,
      properties = _ref.properties;
  return responsiveStyles(_objectSpread({
    display: 'contents'
  }, properties, {}, typographyVariantLabels.reduce(function (rest, variantLabel) {
    var _objectSpread3;

    return _objectSpread({}, rest, (_objectSpread3 = {}, _defineProperty(_objectSpread3, postfix("typography-".concat(variantLabel, "-fontFamily")), function (breakpointLabel) {
      var _a;

      return (_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontFamily;
    }), _defineProperty(_objectSpread3, postfix("typography-".concat(variantLabel, "-fontSize")), function (breakpointLabel) {
      var _a;

      return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontSize, 'px');
    }), _defineProperty(_objectSpread3, postfix("typography-".concat(variantLabel, "-fontStretch")), function (breakpointLabel) {
      var _a;

      return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.fontStretch, '%');
    }), _defineProperty(_objectSpread3, postfix("typography-".concat(variantLabel, "-lineHeight")), function (breakpointLabel) {
      var _a;

      return withUnit((_a = typography[variantLabel][breakpointLabel]) === null || _a === void 0 ? void 0 : _a.lineHeight, 'px');
    }), _objectSpread3));
  }, {}), {}, spacingLabels.reduce(function (rest, spacingLabel) {
    return _objectSpread({}, rest, _defineProperty({}, postfix("space-".concat(spacingLabel)), function (breakpointLabel) {
      return withUnit(spacing[spacingLabel][breakpointLabel], 'px');
    }));
  }, {})), breakpoints);
});
export var ThemeProvider = function ThemeProvider(_ref2) {
  var theme = _ref2.theme,
      children = _ref2.children;

  var breakpoints = theme.breakpoints,
      name = theme.name,
      spacing = theme.spacing,
      typography = theme.typography,
      restTheme = _objectWithoutProperties(theme, ["breakpoints", "name", "spacing", "typography"]);

  var themeFragmentVariablesTree = useMemo(function () {
    return generateCustomPropertyNamesFromTheme(restTheme);
  }, [restTheme]);
  var themeFragmentCustomProperties = useMemo(function () {
    return assignDeepFlat(themeFragmentVariablesTree, restTheme);
  }, [restTheme, themeFragmentVariablesTree]);
  var themeWithVariables = useMemo(function () {
    return _objectSpread({}, themeFragmentVariablesTree, {
      breakpoints: breakpoints,
      name: name,
      responsive: {
        spacing: spacingLabels.reduce(function (rest, spacingLabel) {
          return _objectSpread({}, rest, _defineProperty({}, spacingLabel, postfix("space-".concat(spacingLabel))));
        }, {}),
        typography: typographyVariantLabels.reduce(function (rest, variantLabel) {
          return _objectSpread({}, rest, _defineProperty({}, variantLabel, {
            fontFamily: postfix("typography-".concat(variantLabel, "-fontFamily")),
            fontSize: postfix("typography-".concat(variantLabel, "-fontSize")),
            fontStretch: postfix("typography-".concat(variantLabel, "-fontStretch")),
            lineHeight: postfix("typography-".concat(variantLabel, "-lineHeight"))
          }));
        }, {})
      }
    });
  }, [breakpoints, name, themeFragmentVariablesTree]);
  return React.createElement(CustomPropertyProvider, _objectSpread({
    properties: themeFragmentCustomProperties
  }, theme), React.createElement(StyledThemeProvider, {
    theme: themeWithVariables
  }, children));
};